import { DatePipe, TitleCasePipe } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { AfterViewInit, Component, computed, effect, inject, linkedSignal, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { SplitOnPipe } from "@kno2/common/pipes";
import { DocumentTypesService } from "@kno2/data-access/document-types";
import { IntegrationsService } from "@kno2/data-access/integrations";
import { MessagePatient, PatientIntegrationMeta } from "@kno2/data-access/messages";
import { SettingsService } from "@kno2/data-access/settings";
import { MessageActionBarComponent, MessagePatientAttachmentsComponent, MessagePatientDetailsComponent } from "@kno2/features/message";
import { MessageFormManagerService, MessageFormService } from "@kno2/features/message/shared";
import { ConfirmationDialogService } from "@kno2/shared/confirmation-dialog";
import { NotificationAlertComponent } from "@kno2/shared/notification-alert";
import { PdfViewerContainerComponent, PdfViewerOptions } from "@kno2/shared/pdf-viewer";
import { compare, pick } from "@kno2/utils";
import { ReleaseAddressPanelComponent } from "./address-panel/release-address-panel.component";

@Component({
    selector: "kno2-release-new",
    imports: [
        ReactiveFormsModule,
        MessageActionBarComponent,
        MessagePatientDetailsComponent,
        MessagePatientAttachmentsComponent,
        ReleaseAddressPanelComponent,
        NotificationAlertComponent,
        PdfViewerContainerComponent,
        DatePipe
    ],
    providers: [SplitOnPipe, TitleCasePipe],
    templateUrl: "./release-new.component.html"
})
export class ReleaseNewComponent implements AfterViewInit {
    private readonly route = inject(ActivatedRoute);
    private readonly messageFormService = inject(MessageFormService);
    private readonly messageFormManagerService = inject(MessageFormManagerService);
    private readonly documentTypesService = inject(DocumentTypesService);
    private readonly integrationsService = inject(IntegrationsService);
    private readonly settingsService = inject(SettingsService);
    private readonly confirmationDialogService = inject(ConfirmationDialogService);
    private readonly splitOnPipe = inject(SplitOnPipe);
    private readonly titleCasePipe = inject(TitleCasePipe);

    public form = this.messageFormService.reset().form;
    public patientIntegrationMeta = signal<PatientIntegrationMeta>(null);
    public message = this.route.snapshot.data.message;
    public attachments = this.message?.attachments || [];

    protected interoperabilitySettings = this.settingsService.getInteroperabilitySettings();
    protected deliverySummaryEnableDownload = computed(() => this.interoperabilitySettings()?.deliverySummaryEnableDownload);
    protected showDocumentViewer = signal<boolean>(false);
    protected useIntegrationDocumentTypes = this.route.snapshot.data.useIntegrationDocumentTypes;

    protected error = this.messageFormManagerService.getError;
    protected serverError = computed(() => (this.integrationDocTypesResource.error() as HttpErrorResponse)?.error?.message || null);

    protected selectedAttachment = linkedSignal(() => this.message?.attachments?.[0]);
    protected capabilities = this.integrationsService.getCapabilities();

    private hasFacilityLookup = computed(() => this.capabilities()?.capabilities?.some((cap) => cap.display === "facilityLookup"));
    private facilityIdControl = this.form.get("patient.facilityId");
    private facilityIdValue = toSignal(this.facilityIdControl.valueChanges, { initialValue: this.facilityIdControl.value });
    private defaultDocumentTypes = !this.useIntegrationDocumentTypes ? this.documentTypesService.getDocumentTypes() : null;

    private integrationDocTypesQuery = computed(() => {
        if (!this.useIntegrationDocumentTypes) return undefined;
        if (!this.capabilities()) return undefined;
        if (this.hasFacilityLookup()) {
            const facilityId = this.facilityIdValue();
            if (!facilityId) return undefined;
            return { facilityId: Number(facilityId) };
        }
        return {};
    });

    private integrationDocTypesResource = this.integrationsService.getIntegrationDocumentTypesResource(this.integrationDocTypesQuery);

    protected documentTypes = computed(() => {
        if (!this.useIntegrationDocumentTypes) return this.defaultDocumentTypes?.();
        if (!this.integrationDocTypesQuery()) return undefined;
        if (this.integrationDocTypesResource.error()) return [];
        return this.integrationDocTypesResource.value();
    });

    private readonly releaseTypeChanges = toSignal(this.form.get("releaseType").valueChanges);

    private releaseTypeChangesEffect = effect(() => {
        const releaseType = this.releaseTypeChanges();
        this.messageFormManagerService.applyReleaseType(releaseType);
        this.form.get("subject").setValue(releaseType?.name);
    });

    private readonly attachmentSendTypeChanges = toSignal(this.form.get("attachmentSendType").valueChanges);
    private attachmentSendTypeChangesEffect = effect(() => {
        const attachmentSendType = this.attachmentSendTypeChanges();
        this.messageFormService.toggleAttachmentDocumentTypeValidator(attachmentSendType === "CDA" || attachmentSendType === "PdfCda");
    });

    protected viewerOptions = signal<PdfViewerOptions>({
        rotate: "enabled",
        remove: "enabled",
        print: "hidden",
        download: "hidden",
        zoom: "auto"
    });

    public async ngAfterViewInit(): Promise<void> {
        if (this.message?.id) {
            this.messageFormService.setMessageDetails(this.message, "draft").setPatientDetails(this.message.patient).setAttachments(this.attachments);
        }

        this.messageFormService.addPatientValidators().addReleaseValidators();

        if (this.message?.patient?.integrationMeta) {
            this.patientIntegrationMeta.set(this.message.patient.integrationMeta);
        }

        this.clearError();
    }

    public async extractPatientMeta(patientMeta: MessagePatient): Promise<void> {
        const patientMetaKeys = [
            "patientId",
            "patientIds",
            "firstName",
            "lastName",
            "middleName",
            "birthDate",
            "gender",
            "streetAddress1",
            "streetAddress2",
            "city",
            "state",
            "postalCode",
            "country",
            "telephone"
        ] as (keyof MessagePatient)[];

        const currentPatient = this.form.get("patient").getRawValue() as MessagePatient;
        const meta = {
            ...pick(patientMeta, patientMetaKeys),
            visitId: null,
            visitDate: null
        };

        if (meta.telephone) {
            meta.telephone = meta.telephone.replace(/tel:/, "");
            meta.telephone = meta.telephone.replace(/\+/, "");
        }

        if (!currentPatient || (!currentPatient.firstName && !currentPatient.lastName)) {
            this.form.get("patient").reset(meta);
            return;
        }

        const comparisonResults = compare(currentPatient, meta).filter((prop) => currentPatient[prop]);

        if (!comparisonResults.length) return;

        let message = `<p>Would you like to overwrite the patient details for "${currentPatient.firstName || ""} ${currentPatient.lastName || ""}" with the following:</p>`;
        comparisonResults.forEach((prop) => {
            message += `<div><strong>${this.titleCasePipe.transform(this.splitOnPipe.transform(prop, "uppercase"))}</strong>: ${meta[prop] || "(empty)"}</div>`;
        });

        message += `<p class="mt-3">(Choose 'Cancel' to leave the patient details unchanged.)</p>`;

        this.confirmationDialogService.open({
            headerText: "Multiple Patients",
            bodyText: message,
            confirmFn: () => this.form.get("patient").reset(meta)
        });
    }

    public clearError(): void {
        this.messageFormManagerService.clearError();
    }
}
